<script>
  import { savedGames, createGame } from '$lib/store'
  import AdSlot from '$lib/components/ad-slot.svelte'
  import { ScreenContainer } from '$lib/components/containers'

  import {
    Radio,
    Button,
    Tabs,
    Input,
    Logo,
    Tooltip
  } from '$lib/components/core'
  import AutoComplete from '$c/core/AutoCompleteV2.svelte'

  import Icon from '@iconify/svelte'
  import { File, Dice } from '$icons'

  import Games from '$lib/data/games.json'
  import { DisplayAdSlot } from '$lib/data/ads'
  import { IMG } from '$lib/utils/rewrites'

  import { filterObj } from '$lib/utils/arr'

  let validGames = filterObj(Games, (g) => g.supported)

  let gameName = ''
  const handleNewGame = () => {
    if (!selectedGame?.supported)
      return alert(`Sorry, ${selectedGame?.title} is currently not supported`)

    let createid = selected
    if (selectedGame?.difficulty)
      createid += difficultyOptions?.[difficulty]?.id || ''

    savedGames.update(createGame(gameName, createid))
    window.location = '/game'
  }

  const handleGenGame = () => {
    if (!selectedGame?.supported)
      return alert(`Sorry, ${selectedGame?.title} is currently not supported`)

    fetch(`/api/route/generate/${selectedGame?.pid}.json`)
      .then((res) => res.text())
      .then((res) => {
        let createid = selected
        if (selectedGame?.difficulty)
          createid += difficultyOptions?.[difficulty]?.id || ''

        savedGames.update(createGame(gameName, createid, res))
        window.location = '/game'
      })
  }

  let hoverActive = false
  const togglehover = () => (hoverActive = !hoverActive)

  let selected
  const handleSelect = (id) => () =>
    selected === id ? (selected = null) : (selected = id)

  let difficulty = 0,
    difficultyOptions = []

  let gen = 'All'
  const gens = [
    { label: 'All', val: 'All' },
    { label: 'Rom Hacks', val: 'romhack' }
  ].concat(
    ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX'].map((l) => ({
      label: `Gen ${l}`,
      val: l
    }))
  )

  let customName
  $: {
    if ((selectedGame && !gameName) || customName === gameName) {
      customName = gameName = selectedGame
        ? `${selectedGame?.title} Nuzlocke`
        : ''
    }
  }

  $: difficultyOptions = selectedGame?.difficulty?.map((d) => ({
    id: d.split(':')[1],
    name: d.split(':')[0] || 'Normal'
  }))
  $: selectedGame = validGames[selected]
  $: disabled = !gameName.length || !selected
</script>

<svelte:head>
  <title>Start a Nuzlocke Run | Platinum, Radical Red & 60+ Pokémon Games</title>
  <meta name="robots" content="follow, index" />
  <meta property="og:title" content="Start a Nuzlocke Run | Platinum, Radical Red & 60+ Pokémon Games" />
  <meta name="description" content="Select your game, name your run, and start tracking in seconds. Platinum, Radical Red, FireRed, and 60+ Pokémon games supported — full boss data and encounter tables ready from day one." />
  <meta property="og:description" content="Select your game, name your run, and start tracking in seconds. Platinum, Radical Red, FireRed, and 60+ Pokémon games supported — full boss data and encounter tables ready from day one." />
  <meta property="og:image" content="https://nuzlocketracker.org/newgame.png" />
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:image" content="https://nuzlocketracker.org/newgame.png" />
  {@html `<script type="application/ld+json">${JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "image": "https://nuzlocketracker.org/newgame.png",
    "mainEntity": [
      { "@type": "Question", "name": "Which Pokémon game is best for a first Nuzlocke?", "acceptedAnswer": { "@type": "Answer", "text": "FireRed or Emerald are the most recommended starting points. Both have manageable difficulty, predictable encounter tables, and straightforward routes that teach core Nuzlocke concepts without overwhelming a first-time player. Platinum is excellent for experienced players looking for a harder mainline challenge." } },
      { "@type": "Question", "name": "What is Hardcore mode and which games support it?", "acceptedAnswer": { "@type": "Answer", "text": "Hardcore mode enforces stricter rules: no item use in battle and a hard level cap equal to the next Gym Leader's ace Pokémon. It is available for Radical Red and a handful of other ROM hacks in the tracker. Select the difficulty when creating your run — it cannot be changed mid-run." } },
      { "@type": "Question", "name": "Can I use this tracker for a randomized Nuzlocke?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. The Randomize button pre-generates your entire encounter table with randomized Pokémon before the run starts. It is designed for games like Scarlet & Violet where encounters appear on the overworld and cannot be re-rolled mid-route. For ROM hacks with built-in randomizers, create a standard run and log encounters manually as you find them." } },
      { "@type": "Question", "name": "Which Pokémon games are supported?", "acceptedAnswer": { "@type": "Answer", "text": "Over 60 games are supported, spanning Gen 1 through Gen 9 — including FireRed, Emerald, Platinum, HeartGold, Black 2, X, Sun, Sword, Scarlet, and popular ROM hacks like Radical Red, Blazing Emerald, Renegade Platinum, and more." } },
      { "@type": "Question", "name": "Does the tracker include Gym Leader and boss movesets?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Every supported game includes full boss data: Gym Leader teams, held items, movesets, and abilities. You can review the complete fight before entering battle so there are no surprises." } }
    ]
  })}</script>`}
</svelte:head>

<div class="flex flex-col gap-y-16">
<AdSlot
  adSlot={DisplayAdSlot}
  placement="New game top"
  className="mx-auto mt-12 max-w-5xl px-4 md:mt-2"
/>

<ScreenContainer
  title="Start a Nuzlocke Run"
  icon={File}
  containerClassName={DisplayAdSlot ? '!mt-0 sm:!mt-0' : ''}
  className="mb-20 relative"
>
  <div class="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-y-4">
    <Input
      rounded
      placeholder="Name"
      className="sm:flex-1"
      maxlength={26}
      bind:value={gameName}
    />

    <AutoComplete
      max={Object.keys(validGames).length}
      itemF={(_) => Object.keys(validGames)}
      labelF={(i) => i && Games[i].title}
      placeholder="Game"
      class="block sm:hidden"
      bind:selected
    >
      <div
        class="flex inline-flex h-auto max-h-8 w-full items-center px-2 py-6"
        slot="option"
        let:option={i}
        let:label
      >
        {#if Games[i].logo}
          <Logo
            logo="{Games[i].logo}"
            alt={Games[i].title + ' logo'}
            class="mr-2 w-12"
            role="presentation"
            aspect="192x96"
          />
        {/if}
        {@html label}
      </div>
    </AutoComplete>

    {#if selectedGame?.difficulty}
      <div
        class="my-3 -mr-32 flex flex-auto basis-full flex-col gap-2 md:order-2 md:my-2 md:inline-flex md:flex-row"
      >
        <label
          ><b>Difficulty</b><br /><small class="sm:hidden"
            >This game offers multiple difficulty choices</small
          ></label
        >
        <Radio
          name="difficulty"
          options={difficultyOptions.map((d) => d.name)}
          className="!flex-row gap-x-1"
          bind:selected={difficulty}
        />
      </div>
    {/if}

    <div>
      <Tooltip>Select a game and enter a run name to begin</Tooltip>
      <Button rounded {disabled} onclick={handleNewGame}>Create game</Button>
    </div>
    <div>
      <Tooltip
        >Pre-fills all encounters with randomized Pokémon before your run starts.
        Recommended for games like Scarlet &amp; Violet with overworld encounters.</Tooltip
      >
      <Button
        className="w-full md:w-auto"
        rounded
        {disabled}
        onclick={handleGenGame}
      >
        Randomize
        <Icon inline="true" icon={Dice} class="inline" />
      </Button>
    </div>
  </div>

  <Tabs
    name="gens"
    className="hidden sm:flex"
    tabs={gens}
    bind:selected={gen}
  />

  <ul
    role="radiogroup"
    aria-labelledby="sc_title"
    class="grid hidden grid-cols-3 items-center justify-center gap-x-4 gap-y-6 sm:grid sm:grid-cols-4"
  >
    {#each Object.entries(validGames) as [id, game]}
      {#if game.logo && (gen === 'All' || game.gen === gen)}
        <button
          role="radio"
          aria-checked={selected === id}
          title="Pokemon {game.title}"
          onclick={handleSelect(id)}
          onmouseenter={togglehover}
          onmouseleave={togglehover}
          class="text-wrap group w-full cursor-pointer text-center text-xs font-medium transition-colors hover:text-yellow-500 dark:hover:text-yellow-300"
          class:dark:text-yellow-300={selected === id}
          class:text-yellow-500={selected === id}
        >
          <Logo
            logo="{game.logo}"
            aspect="192x96"
            role="presentation"
            alt={'Pokémon ' + game.title + ' logo'}
            class="mx-auto mb-2 w-24 transition group-hover:grayscale-0 {(selected &&
              selected !== id) ||
            hoverActive
              ? 'grayscale'
              : ''} {selected === id
              ? 'drop-shadow-highlight grayscale-0'
              : ''} cursor-pointer"
          />
          <strong class="mx-auto max-w-[16ch] line-clamp-2">{game.title}</strong
          >
        </button>
      {/if}
    {/each}
  </ul>
</ScreenContainer>
</div>

<div class="seo-wrapper" aria-label="Popular Nuzlocke Games">
  <div class="seo-container">
    <article>
      <p>
        Picking the right game is the first decision of any Nuzlocke.
        The grid above shows every supported title — from Gen 1 classics to competitive ROM hacks —
        organized by generation so you can find your challenge and begin tracking immediately.
        Each game comes with its full encounter tables and boss data loaded from the moment you create your run.
      </p>
      <p>
        Not sure where to start? Beginners usually do best with <strong>FireRed</strong> or <strong>Emerald</strong>,
        while veterans looking for a real test reach for <strong>Platinum</strong> or <strong>Radical Red</strong>.
        The sections below break down what makes each of these a standout Nuzlocke experience.
      </p>

      <h2 id="platinum">Pokémon Platinum Nuzlocke</h2>
      <p>
        <strong>Platinum</strong> is widely considered the best mainline game for a Nuzlocke challenge.
        The Sinnoh region offers varied encounters, and the Gym Leaders — especially <strong>Candice</strong> and <strong>Volkner</strong> —
        have teams that can end a careless run. Cyrus's battles are genuine threats, and the Elite Four
        with Cynthia's infamous Garchomp provide a true endgame test.
      </p>
      <p>
        Using a <strong>Platinum Nuzlocke tracker</strong> is essential here. The game has complex route overlaps
        (multiple encounter tables per area depending on time, place, and progression), and forgetting
        which Pokémon you have already caught is a common mistake. The tracker logs every encounter
        automatically and shows full Gym Leader data including held items and movesets.
        Full coverage for all 210 Sinnohdex Pokémon, all eight Gyms, and the Elite Four is included —
        when facing Cynthia's Garchomp, you will know exactly which team members survive an Earthquake before the battle starts.
      </p>
      <p>
        <a href="/guides/platinum" class="seo-link">Read the full Platinum Nuzlocke Guide →</a>
      </p>

      <h2 id="radical-red">Radical Red Nuzlocke</h2>
      <p>
        <strong>Radical Red</strong> is a ROM hack of FireRed that transforms Kanto into a
        competitive-level challenge. Every trainer has EV-trained Pokémon with optimal movesets,
        held items, and abilities — significantly harder than any mainline game.
      </p>
      <p>
        Preparation is everything here. The tracker includes the full encounter table for all 800+ available Pokémon,
        updated boss teams for each major fight, and a type coverage analyzer that becomes critical
        when every Gym can end your run. It also supports a <strong>Hardcore mode</strong> in the tracker,
        enforcing strict level caps and removing item use in battle — select the difficulty when creating your run.
      </p>
      <p>
        <a href="/guides/radical-red" class="seo-link">Read the full Radical Red Nuzlocke Guide →</a>
      </p>

      <h2 id="fire-red">Pokémon FireRed Nuzlocke</h2>
      <p>
        <strong>FireRed</strong> is the classic starter Nuzlocke game. The Kanto region is compact,
        encounter tables are straightforward, and the difficulty curve is predictable —
        for many players, their first Nuzlocke was a FireRed run.
      </p>
      <p>
        A <strong>FireRed Nuzlocke tracker</strong> helps you avoid the classic pitfalls:
        forgetting to catch something on Route 22 before facing your rival, missing optional Sevii Islands routes,
        or walking into Giovanni's Gym unprepared for the Ground-type onslaught.
        Full boss data for every rival fight, all eight Gyms, and the Elite Four is included,
        with the Physical/Special split applied correctly for FireRed's mechanics.
      </p>
      <p>
        <a href="/guides/fire-red" class="seo-link">Read the full FireRed Nuzlocke Guide →</a>
      </p>

      <h2>More Supported Games</h2>
      <p>Beyond the three above, the tracker supports the complete mainline series and a wide range of ROM hacks:</p>
      <ul class="seo-game-list">
        <li id="heartgold-soulsilver"><strong>HeartGold &amp; SoulSilver</strong> — Johto + Kanto, two full regions in one run; ideal if you want the longest possible Nuzlocke experience</li>
        <li id="black-white"><strong>Black 2 &amp; White 2</strong> — strong level curve and rival fights make this one of the most demanding mainline runs</li>
        <li id="emerald"><strong>Emerald, Ruby &amp; Omega Ruby</strong> — Hoenn's wide encounter pool gives you real team-building flexibility each run</li>
        <li id="scarlet-violet"><strong>Scarlet &amp; Violet</strong> — open-world structure means you control the order of Gyms; use Randomize when creating your run to pre-fill overworld encounters</li>
        <li id="rom-hacks"><strong>ROM Hacks</strong> — Blazing Emerald, Renegade Platinum, Storm Silver, Sacred Gold, Glazed, Unbound, and more; each has modified boss teams already mapped in the tracker</li>
      </ul>
      <p>
        Not sure which to pick? The <a href="/guides" class="seo-link">Guides page</a> has a breakdown of every supported game
        with encounter counts and difficulty notes to help you choose before you commit.
      </p>

      <h2>How to Start</h2>
      <ol class="seo-list">
        <li data-step="1">Select your game from the grid above</li>
        <li data-step="2">Give your run a name</li>
        <li data-step="3">Choose difficulty if the game offers multiple modes</li>
        <li data-step="4">Click "Create game" to start tracking</li>
      </ol>
      <p class="seo-note">
        Already have a run in progress? Head to the <a href="/saves" class="seo-note__link">Load Game</a> screen to continue.
      </p>

      <h2>Frequently Asked Questions</h2>
      <dl class="seo-faq-list">
        <div class="seo-faq-item">
          <dt class="seo-faq-q">Q: Which Pokémon game is best for a first Nuzlocke?</dt>
          <dd class="seo-faq-a">A: FireRed or Emerald are the most recommended starting points. Both have manageable difficulty, predictable encounter tables, and straightforward routes that teach core Nuzlocke concepts without overwhelming a first-time player. Platinum is excellent for experienced players looking for a harder mainline challenge.</dd>
        </div>
        <div class="seo-faq-item">
          <dt class="seo-faq-q">Q: What is Hardcore mode and which games support it?</dt>
          <dd class="seo-faq-a">A: Hardcore mode enforces stricter rules: no item use in battle and a hard level cap equal to the next Gym Leader's ace Pokémon. It is available for Radical Red and a handful of other ROM hacks in the tracker. Select the difficulty when creating your run — it cannot be changed mid-run.</dd>
        </div>
        <div class="seo-faq-item">
          <dt class="seo-faq-q">Q: Can I use this tracker for a randomized Nuzlocke?</dt>
          <dd class="seo-faq-a">A: Yes. The Randomize button pre-generates your entire encounter table with randomized Pokémon before the run starts. It is designed for games like Scarlet &amp; Violet where encounters appear on the overworld and cannot be re-rolled mid-route. For ROM hacks with built-in randomizers, create a standard run and log encounters manually as you find them.</dd>
        </div>
        <div class="seo-faq-item">
          <dt class="seo-faq-q">Q: Which Pokémon games are supported?</dt>
          <dd class="seo-faq-a">A: Over 60 games are supported, spanning Gen 1 through Gen 9 — including FireRed, Emerald, Platinum, HeartGold, Black 2, X, Sun, Sword, Scarlet, and popular ROM hacks like Radical Red, Blazing Emerald, Renegade Platinum, and more.</dd>
        </div>
        <div class="seo-faq-item">
          <dt class="seo-faq-q">Q: Does the tracker include Gym Leader and boss movesets?</dt>
          <dd class="seo-faq-a">A: Yes. Every supported game includes full boss data: Gym Leader teams, held items, movesets, and abilities. You can review the complete fight before entering battle so there are no surprises.</dd>
        </div>
      </dl>
    </article>
  </div>
</div>


<style lang="postcss">
  /* SEO Content Styles */
  .seo-wrapper {
    margin-top: 0;
    padding: 2rem 1rem 5rem;
    background: #eef2ff;
  }

  :global(.dark) .seo-wrapper {
    background: #080d18;
  }

  @media (min-width: 640px) {
    .seo-wrapper {
      padding: 2rem 0 5rem;
    }
  }

  .seo-container {
    @apply overflow-x-hidden mx-auto max-w-prose;
    margin-top: 2rem;
    margin-bottom: 2rem;
    padding: 2rem 1.5rem 2.5rem;
    background: #ffffff;
    border: 1.5px solid rgba(255, 203, 5, 0.45);
    border-radius: 0.875rem;
    box-shadow: 0 2px 12px 0 rgba(255, 203, 5, 0.08);
  }

  @media (min-width: 640px) {
    .seo-container {
      margin-top: 3.5rem;
      margin-bottom: 3.5rem;
      padding: 2.75rem 3rem 3.25rem;
    }
  }

  :global(.dark) .seo-container {
    background: #0f1928;
    border-color: rgba(255, 203, 5, 0.55);
    box-shadow: 0 2px 20px 0 rgba(255, 203, 5, 0.10);
  }

  .seo-container article {
    font-size: 1rem;
    line-height: 1.7;
    color: #374151;
  }

  :global(.dark) .seo-container article {
    color: #c9d5e8;
  }

  .seo-container h1 {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 1.375rem;
    font-weight: 800;
    letter-spacing: -0.01em;
    line-height: 1.35;
    color: #1e3576;
    margin-bottom: 1rem;
  }

  @media (min-width: 640px) {
    .seo-container h1 { font-size: 1.5rem; }
  }

  :global(.dark) .seo-container h1 {
    color: #FFCB05;
  }

  .seo-container h2 {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.875rem;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #2d63ac;
    margin-top: 2.75rem;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #FFCB05;
  }

  :global(.dark) .seo-container h2 {
    color: #5caddd;
    border-bottom-color: #FFCB05;
  }

  .seo-container p {
    margin-bottom: 1rem;
  }

  .seo-container strong {
    font-weight: 600;
    color: #1e3576;
  }

  :global(.dark) .seo-container strong {
    color: #FFCB05;
  }

  .seo-link {
    color: #2d63ac;
    text-decoration: underline;
    text-decoration-color: #FFCB05;
    text-underline-offset: 3px;
    font-weight: 500;
  }

  .seo-link:hover {
    color: #1e3576;
  }

  :global(.dark) .seo-link {
    color: #5caddd;
  }

  :global(.dark) .seo-link:hover {
    color: #7ec8e3;
  }

  .seo-list {
    padding-left: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
    margin: 1.25rem 0 1.5rem;
  }

  .seo-list li {
    position: relative;
    padding: 1rem 1rem 1rem 3.5rem;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 0.625rem;
    line-height: 1.6;
    font-weight: 500;
    color: #1e293b;
    transition: all 0.2s ease;
  }

  .seo-list li:hover {
    background: #f1f5f9;
    border-color: #FFCB05;
    transform: translateX(4px);
  }

  .seo-list li::before {
    content: attr(data-step);
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #FFCB05 0%, #f59e0b 100%);
    color: #1e3576;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.875rem;
    font-weight: 800;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(255, 203, 5, 0.4);
  }

  :global(.dark) .seo-list li {
    background: #1e293b;
    border-color: #334155;
    color: #e2e8f0;
  }

  :global(.dark) .seo-list li:hover {
    background: #0f172a;
    border-color: #FFCB05;
  }

  .seo-note {
    margin-top: 1rem;
    padding: 0.75rem 1rem;
    background: #fffbeb;
    border: 1px solid rgba(255, 203, 5, 0.6);
    border-radius: 0.5rem;
    font-size: 0.9375rem;
    color: #78350f;
  }

  :global(.dark) .seo-note {
    background: #1c1a09;
    border-color: rgba(255, 203, 5, 0.4);
    color: #fde68a;
  }

  .seo-note__link {
    color: #92400e;
    font-weight: 600;
    text-decoration: underline;
    text-decoration-color: #FFCB05;
    text-underline-offset: 3px;
  }

  .seo-note__link:hover {
    color: #78350f;
  }

  :global(.dark) .seo-note__link {
    color: #fcd34d;
  }

  :global(.dark) .seo-note__link:hover {
    color: #fde68a;
  }

  .seo-game-list {
    padding-left: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin: 1rem 0 1.25rem;
  }

  .seo-game-list li {
    padding: 0.625rem 1rem;
    background: #f8fafc;
    border-left: 3px solid #FFCB05;
    border-radius: 0 0.375rem 0.375rem 0;
    font-size: 0.9375rem;
    color: #1e293b;
    line-height: 1.55;
  }

  :global(.dark) .seo-game-list li {
    background: #1e293b;
    color: #e2e8f0;
  }

  /* FAQ within SEO container */
  .seo-faq-list {
    margin-top: 0.75rem;
    display: flex;
    flex-direction: column;
  }

  .seo-faq-item {
    padding: 1rem 0;
    border-bottom: 1px solid #e8edf5;
  }
  .seo-faq-item:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  :global(.dark) .seo-faq-item {
    border-bottom-color: #1e2d45;
  }

  .seo-faq-q {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.9375rem;
    font-weight: 600;
    color: #2d63ac;
    margin-bottom: 0.5rem;
    line-height: 1.45;
    letter-spacing: 0.01em;
  }
  :global(.dark) .seo-faq-q {
    color: #5caddd;
  }

  .seo-faq-a {
    font-size: 0.9375rem;
    color: #4b5563;
    line-height: 1.75;
    margin: 0;
    padding-left: 1rem;
    border-left: 1px solid #FFCB05;
  }
  :global(.dark) .seo-faq-a {
    color: #b8cfe0;
    border-left-color: #FFCB05;
  }
</style>
